import React, { useState, useEffect } from 'react';
import { RotateCcw, CheckSquare, Square, Search, X } from 'lucide-react';
import AttendanceModal from './AttendanceModal';

const AttendanceCalendar = ({ 
  students, 
  selectedMonth, 
  attendanceData, 
  getStudentAttendanceForDate, 
  getAttendanceColor, 
  generateCalendarDays, 
  updateStudentAttendance,
  apiLoading,
  minDate,
  maxDate,
  calendarEvents = [],
  onRefresh,
  searchPlaceholder = "Search students..."
}) => {
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const getEventForDate = (day) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dateStr = date.toDateString();
    return calendarEvents.find(e => new Date(e.date).toDateString() === dateStr);
  };

  const isHoliday = (day) => {
    const event = getEventForDate(day);
    return event?.type === 'holiday' || event?.type === 'school-leave';
  };

  // Reset selections when month or class changes
  useEffect(() => {
    setSelectedCells(new Set());
    setHasChanges(false);
  }, [selectedMonth]);

  // Filter students based on search term
  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleCellClick = (student, date, currentStatus) => {
    // Check if the date is in the future
    const dateObj = new Date(date);
    const today = new Date();
    
    if (dateObj > today) {
      return; // Don't allow selection of future dates
    }

    if (isDateOutOfRange(date)) {
      return; // Don't allow selection of out-of-range dates
    }

    const day = new Date(date).getDate();
    if (isHoliday(day)) {
      return; // Don't allow selection of holidays
    }
    
    const cellKey = `${student._id}-${date}`;
    const newSelectedCells = new Set(selectedCells);
    
    if (newSelectedCells.has(cellKey)) {
      newSelectedCells.delete(cellKey);
    } else {
      newSelectedCells.add(cellKey);
    }
    
    setSelectedCells(newSelectedCells);
    setHasChanges(newSelectedCells.size > 0);
  };

  const handleBulkUpdate = () => {
    if (selectedCells.size === 0) return;
    setModalOpen(true);
  };

  const handleModalUpdate = (status) => {
    const updates = Array.from(selectedCells).map(cellKey => {
      const [studentId, date] = cellKey.split('-');
      return { studentId, date: parseInt(date), status: status };
    });
    
    updateStudentAttendance(updates);
    setSelectedCells(new Set());
    setHasChanges(false);
    setModalOpen(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleClearSelection = () => {
    setSelectedCells(new Set());
    setHasChanges(false);
  };

  const handleRefreshClick = () => {
    handleClearSelection();
    if (onRefresh) onRefresh();
  };

  const handleSelectAll = () => {
    const allCells = new Set();
    const daysInMonth = generateCalendarDays();
    const [year, month] = selectedMonth.split('-').map(Number);
    
    // Use filtered students if search is active, otherwise use all students
    const studentsToSelect = searchTerm ? filteredStudents : students;
    
    studentsToSelect.forEach(student => {
      daysInMonth.forEach(day => {
        // Skip future dates, out of range dates, and holidays
        if (isFutureDate(day) || isDayOutOfRange(day) || isHoliday(day)) return;
        
        const date = new Date(year, month - 1, day);
        const dateInMillis = date.getTime();
        const cellKey = `${student._id}-${dateInMillis}`;
        allCells.add(cellKey);
      });
    });
    
    setSelectedCells(allCells);
    setHasChanges(allCells.size > 0);
  };

  const isAllSelected = () => {
    const daysInMonth = generateCalendarDays();
    const studentsToCheck = searchTerm ? filteredStudents : students;
    
    // Count only non-future and in-range dates
    let totalCells = 0;
    studentsToCheck.forEach(student => {
      daysInMonth.forEach(day => {
        if (!isFutureDate(day) && !isDayOutOfRange(day) && !isHoliday(day)) {
          totalCells++;
        }
      });
    });
    
    return selectedCells.size === totalCells && totalCells > 0;
  };

  const isCellSelected = (student, date) => {
    const cellKey = `${student._id}-${date}`;
    return selectedCells.has(cellKey);
  };

  const handleStudentSelectAll = (student) => {
    const daysInMonth = generateCalendarDays();
    const newSelectedCells = new Set(selectedCells);
    const [year, month] = selectedMonth.split('-').map(Number);
    
    // Check if student is already fully selected (excluding future dates)
    const isFullySelected = isStudentFullySelected(student);
    
    daysInMonth.forEach(day => {
      // Skip future dates, out of range dates, and holidays
      if (isFutureDate(day) || isDayOutOfRange(day) || isHoliday(day)) return;
      
      const date = new Date(year, month - 1, day);
      const dateInMillis = date.getTime();
      const cellKey = `${student._id}-${dateInMillis}`;
      
      if (isFullySelected) {
        // If fully selected, deselect all dates for this student
        newSelectedCells.delete(cellKey);
      } else {
        // If not fully selected, select all dates for this student
        newSelectedCells.add(cellKey);
      }
    });
    
    setSelectedCells(newSelectedCells);
    setHasChanges(newSelectedCells.size > 0);
  };

  const isStudentFullySelected = (student) => {
    const daysInMonth = generateCalendarDays();
    const [year, month] = selectedMonth.split('-').map(Number);
    const studentCells = daysInMonth
      .filter(day => !isFutureDate(day) && !isDayOutOfRange(day) && !isHoliday(day)) // Only include valid dates
      .map(day => {
        const date = new Date(year, month - 1, day);
        const dateInMillis = date.getTime();
        return `${student._id}-${dateInMillis}`;
      });
    
    return studentCells.length > 0 && studentCells.every(cellKey => selectedCells.has(cellKey));
  };

  const handleDateSelectAll = (day) => {
    // Don't allow selection of future, out-of-range, or holidays
    if (isFutureDate(day) || isDayOutOfRange(day) || isHoliday(day)) return;
    
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dateInMillis = date.getTime();
    const newSelectedCells = new Set(selectedCells);
    
    // Check if all students are already selected for this date
    const studentsToSelect = searchTerm ? filteredStudents : students;
    const isDateFullySelectedBoolean = studentsToSelect.every(student => {
      const cellKey = `${student._id}-${dateInMillis}`;
      return selectedCells.has(cellKey);
    });
    
    studentsToSelect.forEach(student => {
      const cellKey = `${student._id}-${dateInMillis}`;
      if (isDateFullySelectedBoolean) {
        // If all students are selected for this date, deselect them
        newSelectedCells.delete(cellKey);
      } else {
        // If not all students are selected, select all for this date
        newSelectedCells.add(cellKey);
      }
    });
    
    setSelectedCells(newSelectedCells);
    setHasChanges(newSelectedCells.size > 0);
  };

  const isDateFullySelected = (day) => {
    // Future, out-of-range, or holiday dates are never considered fully selected
    if (isFutureDate(day) || isDayOutOfRange(day) || isHoliday(day)) return false;
    
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dateInMillis = date.getTime();
    const studentsToCheck = searchTerm ? filteredStudents : students;
    
    return studentsToCheck.every(student => {
      const cellKey = `${student._id}-${dateInMillis}`;
      return selectedCells.has(cellKey);
    });
  };

  const isFutureDate = (day) => {
    // Create the date for the specific day in the selected month
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    // Get today's late night to allow selection of current day
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    return date > today;
  };

  const isDateOutOfRange = (dateInMillis) => {
    if (minDate && dateInMillis < minDate) return true;
    if (maxDate && dateInMillis > maxDate) return true;
    return false;
  };

  const isDayOutOfRange = (day) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return isDateOutOfRange(date.getTime());
  };
  return (
    <div className="bg-white rounded-[2rem] shadow-xl shadow-blue-900/5 border border-slate-100 overflow-hidden animate-slideInUp select-none">
      <div className="p-8">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between mb-8 gap-6">
          <div>
            <h3 className="text-xl font-black text-slate-800 tracking-tight">Attendance Calendar</h3>
            <p className="text-slate-400 text-sm font-medium mt-1">Select cells to perform bulk actions</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Search Bar Wrapped in a premium container */}
            <div className="relative group min-w-[300px]">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
                className="block w-full pl-11 pr-11 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 text-sm font-bold transition-all outline-none"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-rose-500 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex gap-2 h-12">
              <button
                onClick={handleSelectAll}
                disabled={students.length === 0 || apiLoading}
                className={`px-5 rounded-2xl text-sm font-black transition-all flex items-center gap-2 border-2 ${
                  isAllSelected() 
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/30' 
                    : 'bg-white text-slate-600 border-slate-100 hover:border-blue-100 hover:text-blue-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isAllSelected() ? <CheckSquare size={18} /> : <Square size={18} />}
                <span className="hidden sm:inline">{isAllSelected() ? 'Select All' : 'Select All'}</span>
              </button>
              
              <button
                onClick={handleRefreshClick}
                disabled={apiLoading}
                className="px-4 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-blue-600 rounded-2xl border border-slate-100 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                title="Refresh Data"
              >
                <div className={apiLoading ? 'animate-spin' : ''}>
                  <RotateCcw size={18} />
                </div>
              </button>

              <div className="h-12 w-[2px] bg-slate-100 mx-1 hidden sm:block"></div>

              <button
                onClick={handleBulkUpdate}
                disabled={!hasChanges || apiLoading}
                className="px-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-black text-sm shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed disabled:scale-100 flex items-center gap-2"
              >
                {apiLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>Update ({selectedCells.size})</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Selection Info with premium styling */}
        {selectedCells.size > 0 && (
          <div className="mb-8 p-4 bg-blue-50/50 border border-blue-100 rounded-3xl flex flex-wrap items-center justify-between gap-4 animate-fadeIn">
            <p className="text-sm text-blue-900 font-bold flex items-center gap-2 ml-2">
              <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">
                {selectedCells.size}
              </span>
              <span>
                cell{selectedCells.size !== 1 ? 's' : ''} selected
                {isAllSelected() && <span className="ml-2 text-blue-500 font-medium">(Complete Set)</span>}
              </span>
            </p>
            <div className="flex gap-2">
              <button 
                onClick={handleClearSelection}
                className="px-4 py-2 text-xs font-black text-rose-500 hover:bg-rose-50 rounded-xl transition-all uppercase tracking-wider"
              >
                Discard
              </button>
            </div>
          </div>
        )}

        
        {/* Calendar Grid with zero border and premium headers */}
        <div className="overflow-x-auto rounded-3xl border border-slate-100">
          <div className="min-w-max">
            {/* Header with days */}
            <div className="flex bg-slate-50/50 border-b border-slate-100">
              <div className="w-16 p-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-r border-slate-100 flex-shrink-0 text-center">SL</div>
              <div className="w-64 p-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-r border-slate-100 flex-shrink-0 pl-8">Student Name</div>
              {generateCalendarDays().map(day => {
                const [year, month] = selectedMonth.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                const dayName = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
                const isDateSelected = isDateFullySelected(day);
                const dayEvent = getEventForDate(day);
                
                return (
                  <button
                    key={day}
                    onClick={() => handleDateSelectAll(day)}
                    title={dayEvent?.description}
                    className={`w-12 py-3 text-center border-r border-slate-100 flex-shrink-0 transition-all cursor-pointer ${
                      isDateSelected 
                        ? 'bg-blue-600 text-white shadow-inner' 
                        : dayEvent?.type === 'holiday' || dayEvent?.type === 'school-leave'
                          ? 'bg-rose-50 text-rose-600'
                          : dayEvent?.type === 'half-day'
                            ? 'bg-amber-50 text-amber-600'
                            : 'hover:bg-blue-50/50'
                    }`}
                  >
                    <div className={`text-xs font-black ${isDateSelected ? 'text-white' : dayEvent ? 'text-inherit' : 'text-slate-900'}`}>{day.toString().padStart(2, '0')}</div>
                    <div className={`text-[8px] font-black mt-0.5 ${isDateSelected ? 'text-blue-100' : dayEvent ? 'text-inherit' : 'text-slate-400'}`}>{dayName}</div>
                  </button>
                );
              })}
            </div>

            {/* Student rows with premium hover and indicators */}
            {filteredStudents.map((student, index) => {
              const originalIndex = students.findIndex(s => s._id === student._id);
              const isStudentSelected = isStudentFullySelected(student);
              
              return (
                <div key={student._id} className="flex border-b border-slate-100 group">
                  <div className="w-16 p-4 text-xs font-bold text-slate-400 border-r border-slate-100 flex-shrink-0 text-center bg-slate-50/20">{originalIndex + 1}</div>
                <div 
                  className={`w-64 p-4 text-sm border-r border-slate-100 flex-shrink-0 truncate cursor-pointer transition-all flex items-center gap-3 pl-8 ${
                    isStudentSelected 
                      ? 'bg-blue-50/50 text-blue-600 font-black' 
                      : 'text-slate-700 group-hover:bg-slate-50/50'
                  }`}
                  onClick={() => handleStudentSelectAll(student)}
                >
                  <div className={`w-2 h-2 rounded-full transition-all ${isStudentSelected ? 'bg-blue-600 scale-125' : 'bg-slate-200'}`}></div>
                  <span className="truncate">{student.name}</span>
                </div>
                {generateCalendarDays().map(day => {
                  const date = new Date(selectedMonth + '-' + day.toString().padStart(2, '0'));
                  const dateInMillis = date.getTime();
                  const attendanceStatus = getStudentAttendanceForDate(student._id, dateInMillis);
                  const isSelected = isCellSelected(student, dateInMillis);
                  const isFuture = isFutureDate(day);
                  const isOutOfRange = isDayOutOfRange(day);
                  
                  const dayEvent = getEventForDate(day);
                  
                  return (
                    <div key={day} className={`w-12 p-3 border-r border-slate-100 flex justify-center items-center flex-shrink-0 transition-colors ${
                      dayEvent?.type === 'holiday' || dayEvent?.type === 'school-leave' ? 'bg-rose-50/30' :
                      dayEvent?.type === 'half-day' ? 'bg-amber-50/30' :
                      'group-hover:bg-slate-50/30'
                    }`} title={dayEvent?.description}>
                      <button
                        onClick={() => handleCellClick(student, dateInMillis, attendanceStatus)}
                        className={`w-7 h-7 rounded-lg transition-all transform active:scale-90 ${
                          isFuture || isOutOfRange
                            ? 'bg-slate-100 cursor-not-allowed opacity-50'
                            : dayEvent?.type === 'holiday' || dayEvent?.type === 'school-leave'
                              ? 'bg-rose-200 border-2 border-rose-300'
                              : dayEvent?.type === 'half-day'
                                ? 'bg-amber-200 border-2 border-amber-300'
                                : `${getAttendanceColor(attendanceStatus)} hover:brightness-110 shadow-sm`
                        } ${
                          isSelected ? 'ring-2 ring-blue-500 ring-offset-2 scale-110 z-10' : ''
                        }`}
                      >
                        {isSelected && !isFuture && !isOutOfRange && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 bg-white rounded-full shadow-sm"></div>
                          </div>
                        )}
                      </button>
                    </div>
                  );
                })}
                </div>
              );
            })}
            
            {/* No students found message */}
            {filteredStudents.length === 0 && searchTerm && (
              <div className="flex justify-center items-center py-12">
                <div className="text-center">
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No students found</h3>
                  <p className="text-gray-600">No students match your search for "{searchTerm}"</p>
                  <button
                    onClick={clearSearch}
                    className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Clear search
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Attendance Modal */}
      <AttendanceModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onUpdateAttendance={handleModalUpdate}
        loading={apiLoading}
        selectedCount={selectedCells.size}
      />
    </div>
  );
};

export default AttendanceCalendar;
